import { useCallback, useContext, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { AuthContext } from "../../../../contexts/AuthContext";
import { reportService } from "../../../../services/api/reportService";
import { hasChartData } from "../../components/EmptyChart";
import {
    formatDateParam,
    generateChartImage,
    buildPrintHTML,
    toBase64FromPublic,
    buildReportTableSection
} from "../reportUtils";
import { getBackendUrl } from "../../../../utils/env";
import FinanceReportPresenter from "./FinanceReportPresenter";

const PAGE_SIZE = 10;

const FinanceReportTab = () => {
    const { token_client } = useContext(AuthContext);
    const [tempFromDate, setTempFromDate] = useState("");
    const [tempToDate, setTempToDate] = useState("");
    const [loading, setLoading] = useState(true);
    const [exporting, setExporting] = useState(false);

    const [financeReport, setFinanceReport] = useState(null);
    const [tableData, setTableData] = useState([]);
    const [fullData, setFullData] = useState([]);
    const [hasMore, setHasMore] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat("vi-VN").format(amount || 0) + " VNĐ";
    };

    const fetchReport = useCallback(async (fDate, tDate) => {
        try {
            setLoading(true);
            const params = {
                fromDate: formatDateParam(fDate),
                toDate: formatDateParam(tDate),
            };

            const res = await reportService.get_report_client_finance(token_client, params);
            setFinanceReport(res);
            const data = res?.data || [];
            setFullData(data);
            setTableData(data.slice(0, PAGE_SIZE));
            setHasMore(data.length > PAGE_SIZE);
        } catch (err) {
            toast.error("Không thể tải báo cáo tài chính");
        } finally {
            setLoading(false);
        }
    }, [token_client]);

    useEffect(() => {
        fetchReport("", "");
    }, [fetchReport]);

    const loadMoreData = () => {
        if (loadingMore) return;
        setLoadingMore(true);
        setTimeout(() => {
            const currentLength = tableData.length;
            const nextData = fullData.slice(0, currentLength + PAGE_SIZE);
            setTableData(nextData);
            setHasMore(nextData.length < fullData.length);
            setLoadingMore(false);
        }, 500);
    };

    const handleApplyFilter = () => {
        fetchReport(tempFromDate, tempToDate);
    };

    const handleExport = async () => {
        try {
            setExporting(true);
            const chartImg = hasChartData(financeReport?.chart)
                ? await generateChartImage(
                    financeReport.chart,
                    "Biểu đồ tài chính",
                    "Tổng tiền",
                    (v) => v.toLocaleString() + " ₫"
                )
                : "";

            let bodyHTML = `<h1 class="heading">BÁO CÁO TÀI CHÍNH</h1>`;
            if (tempFromDate || tempToDate) {
                bodyHTML += `<div style="font-size:12px;color:#374151;margin-bottom:20px;text-align:center"><b>Thời gian:</b> ${tempFromDate || "..."} → ${tempToDate || "..."}</div>`;
            }

            const tableHTML = `
                <table>
                    <thead>
                        <tr>
                            <th style="width:60px">STT</th>
                            <th>Tên hồ sơ</th>
                            <th style="width:150px;text-align:right">Thành tiền</th>
                            <th style="width:120px">Trạng thái</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${fullData.length > 0
                            ? fullData.map((item, idx) => `
                                <tr>
                                    <td>${idx + 1}</td>
                                    <td>${item.profileName || ""}</td>
                                    <td style="text-align:right">${formatCurrency(item.totalAmount)}</td>
                                    <td>${item.status || ""}</td>
                                </tr>
                            `).join("")
                            : `<tr><td colspan="4" style="text-align:center;padding:20px;color:#6b7280">Không có dữ liệu trong khoảng thời gian này</td></tr>`
                        }
                    </tbody>
                </table>
            `;

            bodyHTML += `
                <div class="report-section">
                    <div style="margin-bottom:12px">
                        <div class="summary-box"><b>Tổng tiền:</b> ${formatCurrency(financeReport?.summary?.totalAmount)}</div>
                        <div class="summary-box"><b>Đã chi:</b> ${formatCurrency(financeReport?.summary?.paidAmount)}</div>
                        <div class="summary-box"><b>Chưa thanh toán:</b> ${formatCurrency(financeReport?.summary?.unpaidAmount)}</div>
                    </div>
                    ${chartImg ? `<div class="chart-container"><img src="${chartImg}" alt="Chart" /></div>` : ""}
                </div>
                ${buildReportTableSection(tableHTML)}
            `;

            const html = buildPrintHTML(bodyHTML, "Báo cáo tài chính");
            const watermarkBase64 = await toBase64FromPublic("/images/watermark-45.png");
            const BE_URL = getBackendUrl();

            const response = await fetch(`${BE_URL}/pdf/generate`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token_client}`,
                },
                body: JSON.stringify({
                    html,
                    profileName: "Báo cáo tài chính",
                    watermarkBase64,
                    watermarkOpacity: 0.12,
                    options: { width: 1123, height: 794, deviceScaleFactor: 2 },
                    watermarkMode: "fixed",
                }),
            });

            if (!response.ok) throw new Error("Không thể tạo PDF");

            const blob = await response.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `Bao-cao-tai-chinh-${Date.now()}.pdf`;
            a.click();
            URL.revokeObjectURL(url);
            toast.success("Xuất báo cáo thành công!");
        } catch (err) {
            console.error("Export error:", err);
            toast.error("Xuất PDF thất bại");
        } finally {
            setExporting(false);
        }
    };

    return (
        <FinanceReportPresenter
            loading={loading}
            exporting={exporting}
            tempFromDate={tempFromDate}
            tempToDate={tempToDate}
            setTempFromDate={setTempFromDate}
            setTempToDate={setTempToDate}
            handleApplyFilter={handleApplyFilter}
            handleExport={handleExport}
            financeReport={financeReport}
            tableData={tableData}
            hasMore={hasMore}
            loadingMore={loadingMore}
            loadMoreData={loadMoreData}
            formatCurrency={formatCurrency}
        />
    );
};

export default FinanceReportTab;
